import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  Ship,
  FileCheck2,
  Truck,
  PackageCheck,
  BarChart3,
  ArrowUpRight,
} from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const SERVICE_META = [
  { icon: Search, span: "md:col-span-7", accent: false },
  { icon: Ship, span: "md:col-span-5", accent: true },
  { icon: FileCheck2, span: "md:col-span-4", accent: false },
  { icon: Truck, span: "md:col-span-4", accent: false },
  { icon: PackageCheck, span: "md:col-span-4", accent: false },
  { icon: BarChart3, span: "md:col-span-12", accent: false, wide: true },
];

export default function Services() {
  const { t } = useT();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-white py-28 lg:py-40 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="overline">{t.services.overline}</span>
              <div className="h-px w-16 bg-slate-300" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.02] text-slate-900">
              {t.services.title}
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 lg:pt-6"
          >
            <p className="text-lg text-slate-600 leading-relaxed">
              {t.services.subtitle}{" "}
              <span className="text-slate-900 font-semibold">
                {t.services.subtitleBold}
              </span>{" "}
              {t.services.subtitleEnd}
            </p>
          </motion.div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
          {t.services.items.map((item, i) => {
            const meta = SERVICE_META[i];
            const Icon = meta.icon;
            const s = { ...meta, ...item };
            return (
            <motion.article
              key={s.title}
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`${s.span} relative group rounded-2xl border p-7 lg:p-8 transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
                s.accent
                  ? "bg-slate-900 text-white border-slate-900 hover:shadow-[0_20px_60px_-15px_rgba(15,23,42,0.45)]"
                  : "bg-[#F8F9FA] border-slate-200 hover:border-slate-300 hover:shadow-[0_16px_50px_-20px_rgba(15,23,42,0.18)]"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-colors ${
                  s.accent
                    ? "bg-[#FF6B00] text-white"
                    : "bg-white border border-slate-200 text-[#005BEA] group-hover:bg-[#005BEA] group-hover:text-white group-hover:border-[#005BEA]"
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={2.3} />
              </div>

              <div className={s.wide ? "flex flex-col md:flex-row md:items-end md:justify-between gap-6" : ""}>
                <div className={s.wide ? "max-w-2xl" : ""}>
                  <h3
                    className={`font-display text-xl lg:text-[22px] font-semibold tracking-tight mb-3 ${
                      s.accent ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`text-[15px] leading-relaxed ${
                      s.accent ? "text-white/75" : "text-slate-600"
                    }`}
                  >
                    {s.desc}
                  </p>
                </div>
                {s.wide && (
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#005BEA] hover:gap-3 transition-all"
                  >
                    {t.services.ctaWide}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
                  </a>
                )}
              </div>

              <div
                className={`absolute top-7 right-7 w-7 h-7 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 ${
                  s.accent ? "bg-white text-slate-900" : "bg-slate-900 text-white"
                }`}
              >
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </div>
            </motion.article>
          );
          })}
        </div>
      </div>
    </section>
  );
}
